import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';

import { ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  GoogleAnalyticsRouteEventsService,
  McGlobalsModule,
  McGlobalsServiceConfig,
  McLayoutModule,
  McNavModule,
  McSessionTimeoutApiService,
  McSessionTimeoutsModule,
}
  from '@medrio/common';
import {
  AppConfigLoaderService,
  Context,
  CrossStudyPortalAppConfig,
  CrossStudyPortalAppPathsService,
  MedrioCrossStudyPortalModule,
} from 'projects/cross-study-portal-lib/src/public-api';
import { FlexLayoutModule } from '@angular/flex-layout';
import { HttpStatus401Interceptor } from './interceptors/http-status-401.interceptor';
import { HttpStatus403Interceptor } from './interceptors/http-status-403.interceptor';
import { HttpStatus500Interceptor } from './interceptors/http-status-500.interceptor';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HeaderComponent } from './components/header/header.component';
import { HttpStatus401Component } from './components/http-status401/http-status401.component';
import { TokenInterceptor } from './interceptors/token.interceptor';
import { ContextInitializerService } from '../../../cross-study-portal-lib/src/lib/services/context-initializer.service';
import { ContextInitializationResponseHandlerService } from './services/context-initialization-response-handler.service';
import { ApplicationInitializerService } from './services/application-initializer.service';
import { UserMenuComponent } from './components/user-menu/user-menu.component';
import { StudySelectComponent } from './components/study-select/study-select.component';
import { LogoutComponent } from './components/logout/logout.component';
import { HelpMenuComponent } from './components/help-menu/help-menu.component';
import { LandingPageComponent } from './components/landing-page/landing-page.component';
import { AppsMenuComponent } from './components/apps-menu/apps-menu.component';
import { NoAccessComponent } from './components/no-access/no-access.component';
import { TimeoutInterceptor } from './interceptors/timeout.interceptor';
import { LoaderInterceptorService } from './interceptors/loader.interceptor';

const globals: McGlobalsServiceConfig = {
  study: {},
  resource: [],
  urls: {
    webSiteBaseUrl: '/',
    apiUrl: '',
  },
  timeoutUrls: {
    getTimeout: '/api/timeout/getTimeout',
    renewTimeout: '/api/timeout/renewTimeout',
    reloginUser: '/api/timeout/reloginUser',
  },
};

const loadingExceptionHandler = (msg: string, err: Error) => {
  console.error(msg, err);
  if (!document) return;
  const appRoot = document.getElementsByTagName('app-root');
  if (!appRoot.length) return;
  appRoot[0].classList.add('failed');
};

// eslint-disable-next-line max-len
const loadAppConfig = (appConfigLoader: AppConfigLoaderService<CrossStudyPortalAppConfig>, appConfigPath: CrossStudyPortalAppPathsService) => async () => {
  try {
    return appConfigLoader.loadAppConfig(appConfigPath);
  } catch (err) {
    loadingExceptionHandler('Failed to load app-config', err as Error);
    throw err;
  }
};

const initContext = (
  contextService: ContextInitializerService<Context>,
  contextInitializationResponseHandlerService: ContextInitializationResponseHandlerService,
  appConfigLoader: AppConfigLoaderService<CrossStudyPortalAppConfig>,
  config: CrossStudyPortalAppPathsService) => async () => {
  try {
    const initContextResponse = await contextService.initContext(appConfigLoader, config);
    return contextInitializationResponseHandlerService.handler(initContextResponse, appConfigLoader);
  } catch (err) {
    loadingExceptionHandler('Failed to initialize context', err as Error);
    throw err;
  }
};

// eslint-disable-next-line max-len
const setupServices = (contextService: ContextInitializerService<Context>, initApp: ApplicationInitializerService) => () => initApp.init(contextService);

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export const ROUTE_ROLE = 'roles';

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    HttpStatus401Component,
    StudySelectComponent,
    LogoutComponent,
    UserMenuComponent,
    HelpMenuComponent,
    LandingPageComponent,
    AppsMenuComponent,
    NoAccessComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    MatDialogModule,
    MatFormFieldModule,
    McNavModule,
    MatInputModule,
    MatIconModule,
    MatSnackBarModule,
    MatButtonModule,
    McLayoutModule,
    FlexLayoutModule,
    McSessionTimeoutsModule.forRoot(),
    McGlobalsModule.forRoot(globals),
    MedrioCrossStudyPortalModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (createTranslateLoader),
        deps: [HttpClient],
      },
      defaultLanguage: 'en',
    }),
  ],
  providers: [
    AppConfigLoaderService,
    ApplicationInitializerService,
    {
      provide: APP_INITIALIZER,
      useFactory: loadAppConfig,
      multi: true,
      deps: [AppConfigLoaderService, CrossStudyPortalAppPathsService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initContext,
      multi: true,
      deps: [
        ContextInitializerService,
        ContextInitializationResponseHandlerService,
        AppConfigLoaderService,
        CrossStudyPortalAppPathsService,
      ],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: setupServices,
      multi: true,
      deps: [ContextInitializerService, ApplicationInitializerService],
    },
    { provide: Window, useValue: window },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpStatus401Interceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpStatus403Interceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpStatus500Interceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TimeoutInterceptor,
      multi: true,
    },
    McSessionTimeoutApiService,
    { provide: McGlobalsServiceConfig, useValue: globals },
    GoogleAnalyticsRouteEventsService,
  ],
  bootstrap: [AppComponent],
  exports: [],
})

export class AppModule {
  constructor(private ga: GoogleAnalyticsRouteEventsService) { }
}
